import { Zeeguu_API } from "./classDef";
import { AUDIO_STATUS } from "../dailyAudio/AudioLessonConstants";
import * as Sentry from "@sentry/react";

function getTimezoneOffsetMinutes() {
  return new Date().getTimezoneOffset() * -1;
}

Zeeguu_API.prototype.getTodaysLesson = function (callback, onError) {
  this.apiLog("GET get_todays_lesson");
  
  const timezoneOffset = getTimezoneOffsetMinutes();
  
  fetch(this._appendSessionToUrl(`get_todays_lesson?timezone_offset=${timezoneOffset}`))
    .then((response) => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || "Network response was not ok");
        });
      }
      return response.json();
    })
    .then((data) => {
      if (data.lesson === null) {
        // No lesson for today, call callback with null to indicate no lesson
        callback(null);
      } else if (data.audio_url) {
        // Convert relative audio URL to full URL with session
        const audioUrl = `${this.baseAPIurl}${data.audio_url}?session=${this.session}`;
        callback({ ...data, audio_url: audioUrl });
      } else {
        throw new Error("No audio URL in response");
      }
    })
    .catch((error) => {
      if (!error.message.includes("No lesson generated yet today")) {
        console.error("Error getting today's lesson:", error);
        Sentry.captureException(error, { tags: { endpoint: "get_todays_lesson" } });
      }
      if (onError) {
        onError(error);
      }
    });
};

Zeeguu_API.prototype.generateDailyLesson = function (callback, onError, suggestion, suggestionType) {
  this.apiLog("POST generate_daily_lesson");

  const formData = new FormData();
  const timezoneOffset = getTimezoneOffsetMinutes();
  formData.append("timezone_offset", timezoneOffset);
  if (suggestion) {
    formData.append("suggestion", suggestion);
    if (suggestionType) {
      formData.append("suggestion_type", suggestionType);
    }
  }

  fetch(this._appendSessionToUrl("generate_daily_lesson"), {
    method: "POST",
    body: formData,
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || "Network response was not ok");
        });
      }
      return response.json();
    })
    .then((data) => {
      if (data.status === AUDIO_STATUS.GENERATING) {
        // Generation started in background — polling will handle the rest
        callback(data);
      } else if (data.audio_url) {
        // Existing lesson returned directly
        const audioUrl = `${this.baseAPIurl}${data.audio_url}?session=${this.session}`;
        callback({ ...data, audio_url: audioUrl });
      } else {
        throw new Error("No audio URL in response");
      }
    })
    .catch((error) => {
      console.error("Error generating daily lesson:", error);
      Sentry.captureException(error, { tags: { endpoint: "generate_daily_lesson" } });
      if (onError) {
        onError(error);
      }
    });
};

Zeeguu_API.prototype.deleteTodaysLesson = function (callback, onError) {
  this.apiLog("DELETE delete_todays_lesson");
  
  const timezoneOffset = getTimezoneOffsetMinutes();
  
  fetch(this._appendSessionToUrl(`delete_todays_lesson?timezone_offset=${timezoneOffset}`), {
    method: "DELETE",
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || "Network response was not ok");
        });
      }
      return response.json();
    })
    .then((data) => {
      callback(data);
    })
    .catch((error) => {
      console.error("Error deleting today's lesson:", error);
      Sentry.captureException(error, { tags: { endpoint: "delete_todays_lesson" } });
      if (onError) {
        onError(error);
      }
    });
};

Zeeguu_API.prototype.getPastDailyLessons = function (limit, offset, callback, onError) {
  this.apiLog("GET past_daily_lessons");
  
  let url = "past_daily_lessons";
  const params = [];
  if (limit) params.push(`limit=${limit}`);
  if (offset) params.push(`offset=${offset}`);
  
  const timezoneOffset = getTimezoneOffsetMinutes();
  params.push(`timezone_offset=${timezoneOffset}`);
  
  if (params.length > 0) {
    // Check if URL already has query parameters
    url += url.includes('?') ? `&${params.join('&')}` : `?${params.join('&')}`;
  }
  
  fetch(this._appendSessionToUrl(url))
    .then((response) => {
      if (!response.ok) {
        // Try to parse as JSON first, but handle HTML responses
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          return response.json().then(data => {
            throw new Error(data.error || "Network response was not ok");
          });
        } else {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }
      return response.json();
    })
    .then((data) => {
      callback(data);
    })
    .catch((error) => {
      console.error("Error getting past daily lessons:", error);
      Sentry.captureException(error, { tags: { endpoint: "past_daily_lessons" } });
      if (onError) {
        onError(error);
      }
    });
};

Zeeguu_API.prototype.updateLessonState = function (lessonId, action, positionSeconds, callback, onError) {
  this.apiLog(`POST update_lesson_state/${lessonId}`);
  
  const payload = { action };
  
  // Add position_seconds for pause action
  if (action === 'pause' && positionSeconds !== undefined) {
    payload.position_seconds = Math.floor(positionSeconds);
  }
  
  fetch(this._appendSessionToUrl(`update_lesson_state/${lessonId}`), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || "Network response was not ok");
        });
      }
      return response.json();
    })
    .then((data) => {
      if (callback) callback(data);
    })
    .catch((error) => {
      console.error("Error updating lesson state:", error);
      Sentry.captureException(error, { tags: { endpoint: "update_lesson_state" } });
      if (onError) {
        onError(error);
      }
    });
};

Zeeguu_API.prototype.checkDailyLessonFeasibility = function (callback, onError) {
  this.apiLog("GET check_daily_lesson_feasibility");
  
  fetch(this._appendSessionToUrl("check_daily_lesson_feasibility"))
    .then((response) => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || "Network response was not ok");
        });
      }
      return response.json();
    })
    .then((data) => {
      callback(data);
    })
    .catch((error) => {
      console.error("Error checking daily lesson feasibility:", error);
      if (onError) {
        onError(error);
      }
    });
};

// Removed saveLessonProgress - use updateLessonState with "pause" action instead

Zeeguu_API.prototype.getAudioLessonGenerationProgress = function (callback, onError) {
  this.apiLog("GET audio_lesson_generation_progress");

  fetch(this._appendSessionToUrl("audio_lesson_generation_progress"))
    .then((response) => {
      if (!response.ok) {
        return response.json().then(data => {
          throw new Error(data.error || "Network response was not ok");
        });
      }
      return response.json();
    })
    .then((data) => {
      callback(data.progress); // Returns null if no generation in progress
    })
    .catch((error) => {
      console.error("Error getting generation progress:", error);
      if (onError) {
        onError(error);
      }
    });
};